import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import BookIcon from "@mui/icons-material/Book";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { motion } from "framer-motion";
import { RootState } from "../../../../../store/Index";
import { setSelectedBook } from "../../../../../store/slice/MemoryBookSlice";
import { fetchAssignedMemoryBooks } from "./service/memoryBook.api";
import MemoryBookLoginModal from "./MemoryBookLoginModal";
import MemoryBookUpdateModal from "./MemoryBookUpdateModal";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const MemoryBookDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const { selectedBook, dailyGoal } = useSelector(
    (state: RootState) => state.memoryBook,
  );
  const user = useSelector((state: RootState) => state.auth.user);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const state = location.state as { showModal?: boolean };
    if (state?.showModal) {
      setShowLoginModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const validateAssignment = async () => {
      if (!user?.id || !selectedBook) return;

      try {
        const res = await fetchAssignedMemoryBooks(user.id);
        const results = res.data?.results ?? [];

        const assignment = results.find(
          (item) => String(item.stream?.id) === selectedBook.id,
        );

        if (!assignment) {
          dispatch(setSelectedBook(null));
          setShowLoginModal(true);
        }
      } catch {
        toast.error("Failed to check assignment");
      }
    };

    validateAssignment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedBookId = selectedBook?.id;

  const handleUpdateSuccess = useCallback(async () => {
    if (!selectedBookId || !user?.id) return;

    try {
      const res = await fetchAssignedMemoryBooks(user.id);
      const results = res.data?.results ?? [];

      const assignment = results.find(
        (item) => String(item.stream?.id) === selectedBookId,
      );

      if (!assignment) {
        dispatch(setSelectedBook(null));
        setShowLoginModal(true);
        return;
      }

      const physicalComments = assignment.physical_comments || 0;
      const doneComments = assignment.done_comments || 0;
      const unfinished = Math.max(physicalComments - doneComments, 0);

      const deceasedName = assignment.stream?.booking?.deceased?.[0]
        ? `${assignment.stream.booking.deceased[0].first_name} ${assignment.stream.booking.deceased[0].surname}`
        : "Unnamed Book";

      const streamTitle = assignment.stream?.title || "Untitled Stream";
      const deceasedPhoto =
        assignment.stream?.booking?.deceased?.[0]?.person_image;

      dispatch(
        setSelectedBook({
          id: String(assignment.stream?.id),
          assignmentId: assignment.id,
          title: streamTitle || deceasedName,
          thumbnail: deceasedPhoto,
          online_comments: 0,
          physical_comments: physicalComments,
          total_comments: physicalComments,
          unfinished_comments: unfinished,
          dueDate: assignment.due_date,
          status:
            unfinished === 0
              ? "completed"
              : doneComments > 0
                ? "in_progress"
                : "not_started",
        }),
      );

      setShowUpdateModal(false);
    } catch (error) {
      console.error("Failed to refresh book data:", error);
      toast.error("Failed to refresh book data");
    }
  }, [selectedBookId, user?.id, dispatch]);

  const statsCards = [
    {
      title: "Current Book",
      value: selectedBook?.title || "No book selected",
      icon: BookIcon,
      color: "#007AFF",
    },
    {
      title: "Today's Goal",
      value: `${dailyGoal} comments`,
      icon: AssignmentIcon,
      color: "#4BA663",
    },
    {
      title: "Remaining",
      value: selectedBook
        ? `${selectedBook.unfinished_comments} comments`
        : "N/A",
      icon: TrendingUpIcon,
      color: "#FFA500",
    },
  ];

  const handleUpdateScanned = () => {
    setShowUpdateModal(true);
  };

  const progress = selectedBook
    ? ((selectedBook.total_comments - selectedBook.unfinished_comments) /
        selectedBook.total_comments) *
      100
    : 0;

  const isCompleted = selectedBook
    ? selectedBook.unfinished_comments === 0
    : false;

  if (!selectedBook) {
    return (
      <>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: "center",
            maxWidth: "100%",
            mx: "auto",
            mt: 8,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            No Book Selected
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {
              "You haven't selected a memory book yet. Please choose one to start working."
            }
          </Typography>
          <Button
            variant="contained"
            onClick={() => setShowLoginModal(true)}
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${
                reduxTheme.primary_colors?.[500] || "#503629"
              } 0%, ${reduxTheme.primary_colors?.[400] || "#614438"} 100%)`,
            }}
          >
            Select a Memory Book
          </Button>
        </Paper>
        <MemoryBookLoginModal
          open={showLoginModal}
          onClose={() => setShowLoginModal(false)}
        />
      </>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            Welcome back, {user?.first_name}!
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={() => setShowLoginModal(true)}
          sx={{
            textTransform: "none",
            borderRadius: "12px",
            px: 3,
            py: 1.5,
            borderColor: reduxTheme.primary_colors?.[400] || "#614438",
            color: reduxTheme.primary_colors?.[400] || "#614438",
            "&:hover": {
              borderColor: reduxTheme.primary_colors?.[500] || "#503629",
              backgroundColor: `${reduxTheme.primary_colors?.[400]}10`,
            },
          }}
        >
          Switch Book
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid size={{ xs: 12, md: 4 }} key={index}>
            <Card
              component={motion.div}
              whileHover={{ y: -5 }}
              elevation={0}
              sx={{
                borderRadius: 3,
                boxShadow: "none",
                transition: "box-shadow 0.3s ease",
                "&:hover": {
                  boxShadow: `0 4px 20px ${stat.color}33`,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: `${stat.color}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <stat.icon sx={{ color: stat.color, fontSize: 28 }} />
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    {stat.title}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Current Book Progress */}
      {selectedBook && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: "none",
            mb: 4,
            position: "relative",
          }}
        >
          <Chip
            label={isCompleted ? "Completed" : "In Progress"}
            size="small"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              fontWeight: 600,
              backgroundColor: isCompleted ? "#4BA66320" : "#FFA50020",
              color: isCompleted ? "#4BA663" : "#FFA500",
              border: `1px solid ${isCompleted ? "#4BA663" : "#FFA500"}`,
            }}
          />

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, pr: 12 }}>
            {selectedBook.title}
          </Typography>

          <Box sx={{ mb: 1 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body1" color="text.secondary">
                Completed
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {selectedBook.total_comments - selectedBook.unfinished_comments}{" "}
                / {selectedBook.total_comments} ({progress.toFixed(1)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 12,
                borderRadius: 2,
                backgroundColor: reduxTheme.neutral_colors?.[800],
                "& .MuiLinearProgress-bar": {
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${
                    reduxTheme.primary_colors?.[400]
                  } 0%, ${reduxTheme.primary_colors?.[500]} 100%)`,
                },
              }}
            />
            <Button
              variant="contained"
              fullWidth
              component={motion.button}
              onClick={handleUpdateScanned}
              disabled={isCompleted}
              sx={{
                mt: 1,
                textTransform: "none",
                borderRadius: "8px",
                background: `linear-gradient(135deg, ${
                  reduxTheme.primary_colors?.[400]
                } 0%, ${reduxTheme.primary_colors?.[500]} 100%)`,
                "&:disabled": {
                  background: `rgba(${
                    reduxTheme.primary_colors?.[400]
                      ? parseInt(reduxTheme.primary_colors[400].slice(1, 3), 16)
                      : 97
                  }, ${
                    reduxTheme.primary_colors?.[400]
                      ? parseInt(reduxTheme.primary_colors[400].slice(3, 5), 16)
                      : 68
                  }, ${
                    reduxTheme.primary_colors?.[400]
                      ? parseInt(reduxTheme.primary_colors[400].slice(5, 7), 16)
                      : 56
                  }, 0.3)`,
                },
              }}
            >
              {isCompleted ? "All Comments Scanned" : "Update Scanned Comments"}
            </Button>
          </Box>
        </Paper>
      )}
      <MemoryBookLoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      <MemoryBookUpdateModal
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSuccess={handleUpdateSuccess}
      />
    </Box>
  );
};

export default MemoryBookDashboard;
